/**
 * CompanyUpdateView — render the AI-generated Company Update markdown.
 *
 * Tiny inline renderer that handles the subset Claude emits in the
 * /ai-company-update prompt:
 *   - ## Section headers
 *   - **bold**
 *   - - bullet lines (one level deep)
 *   - paragraphs separated by blank lines
 *   - inline `code` and links (basic [text](url))
 *
 * Not a full markdown parser. If we ever need tables / nested lists /
 * code fences we can swap in react-markdown — keeping it inline for
 * now means zero new dependencies.
 */
export default function CompanyUpdateView({ markdown }) {
  if (!markdown) return null
  const blocks = parseBlocks(String(markdown))
  return (
    <div
      style={{
        fontSize: 14,
        lineHeight: 1.65,
        color: 'var(--text)',
      }}
    >
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  )
}

/* ─── Block parser ───────────────────────────────────────────────────── */

function parseBlocks(src) {
  const lines = src.split(/\r?\n/)
  const blocks = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()
    if (!trimmed) {
      i += 1
      continue
    }
    // ATX heading (# / ## / ###)
    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(trimmed)
    if (headingMatch) {
      blocks.push({ kind: 'heading', level: headingMatch[1].length, text: headingMatch[2] })
      i += 1
      continue
    }
    // Bullet list — collect consecutive `-` or `*` lines
    if (/^[-*]\s+/.test(trimmed)) {
      const items = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''))
        i += 1
      }
      blocks.push({ kind: 'list', items })
      continue
    }
    // Paragraph — collect until blank line or block boundary
    const paraLines = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3}\s+|[-*]\s+)/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i])
      i += 1
    }
    blocks.push({ kind: 'paragraph', text: paraLines.join(' ') })
  }
  return blocks
}

/* ─── Renderer ───────────────────────────────────────────────────────── */

function renderBlock(b, key) {
  if (b.kind === 'heading') {
    const sizes = { 1: 18, 2: 16, 3: 14 }
    const margins = { 1: '20px 0 8px', 2: '18px 0 8px', 3: '14px 0 6px' }
    const Tag = b.level === 1 ? 'h2' : b.level === 2 ? 'h3' : 'h4'
    return (
      <Tag
        key={key}
        style={{
          fontSize: sizes[b.level] || 14,
          fontWeight: 600,
          color: 'var(--text)',
          margin: margins[b.level] || '12px 0 6px',
          letterSpacing: 0,
        }}
      >
        {renderInline(b.text)}
      </Tag>
    )
  }
  if (b.kind === 'list') {
    return (
      <ul
        key={key}
        style={{
          margin: '0 0 12px',
          paddingLeft: 22,
        }}
      >
        {b.items.map((item, j) => (
          <li key={j} style={{ marginBottom: 4 }}>
            {renderInline(item)}
          </li>
        ))}
      </ul>
    )
  }
  // paragraph
  return (
    <p key={key} style={{ margin: '0 0 12px' }}>
      {renderInline(b.text)}
    </p>
  )
}

/* ─── Inline (bold, links, code) ─────────────────────────────────────── */

function renderInline(text) {
  // Walk the string and emit React nodes for **bold**, [text](url), `code`,
  // leaving the rest as plain text. Simple state-machine pass — not robust
  // against nested constructs, but Claude's outputs in this prompt don't
  // produce nested formatting.
  const out = []
  let buf = ''
  let i = 0
  let key = 0
  const flush = () => {
    if (buf) {
      out.push(buf)
      buf = ''
    }
  }
  while (i < text.length) {
    const rest = text.slice(i)
    // **bold**
    const bold = /^\*\*([^*]+)\*\*/.exec(rest)
    if (bold) {
      flush()
      out.push(
        <strong key={key++} style={{ fontWeight: 600 }}>
          {bold[1]}
        </strong>
      )
      i += bold[0].length
      continue
    }
    // [text](url)
    const link = /^\[([^\]]+)\]\(([^)]+)\)/.exec(rest)
    if (link) {
      flush()
      out.push(
        <a
          key={key++}
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent)', textDecoration: 'underline' }}
        >
          {link[1]}
        </a>
      )
      i += link[0].length
      continue
    }
    // `code`
    const code = /^`([^`]+)`/.exec(rest)
    if (code) {
      flush()
      out.push(
        <code
          key={key++}
          style={{
            fontFamily: 'ui-monospace, monospace',
            background: 'var(--background-soft, #f3f4f6)',
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: '0.9em',
          }}
        >
          {code[1]}
        </code>
      )
      i += code[0].length
      continue
    }
    buf += text[i]
    i += 1
  }
  flush()
  return out
}
